import useStory, { span } from './useStory';
import { Explainer } from './GuideParts';

// GUIDE 7'S ANIMATION (26 Oct 2026): the same four-room home twice. First
// with split ACs: an outdoor unit on the outside wall for every room. Then
// with VRV: one outdoor unit on the terrace, a pipe down a shaft and into
// each room's ceiling, the balcony wall left clear. A drawing, not to scale.
// Plays once in view, rests on the VRV picture, Replay.

const ROOMS = [
  { x: 60, y: 70 }, { x: 160, y: 70 },
  { x: 60, y: 150 }, { x: 160, y: 150 },
];

export default function VrvHouse({ watch, sources }) {
  const story = useStory(18000);
  const t = story.t;
  const splitIn = (i) => span(t, 0.12 + i * 0.07, 0.18 + i * 0.07);
  const out = span(t, 0.46, 0.56);          // split units leave
  const vrv = span(t, 0.5, 0.6);            // the VRV unit arrives
  const pipe = span(t, 0.58, 0.78);         // the pipe draws
  const grille = span(t, 0.74, 0.86);       // ceiling units show
  const outdoor = t < 0.5 ? ROOMS.filter((_, i) => splitIn(i) > 0.5).length : 1;

  return (
    <Explainer title={watch.title} captions={watch.captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <div className="grid gap-2">
        <svg viewBox="0 0 330 250" width="100%" role="img" className="block h-auto rounded-xl border border-gray-200 bg-gray-50"
          aria-label="A four-room home. With split ACs, four outdoor units hang on the outside wall. With VRV, one outdoor unit sits on the terrace and the balcony wall is clear.">
          <rect x="40" y="40" width="200" height="190" fill="#fff" stroke="#9ca3af" strokeWidth="2" />
          <line x1="40" x2="240" y1="130" y2="130" stroke="#9ca3af" strokeWidth="2" />
          <line x1="140" x2="140" y1="40" y2="230" stroke="#d1d5db" strokeWidth="1.5" />
          <line x1="20" x2="310" y1="230" y2="230" stroke="#6b7280" strokeWidth="2" />
          <text x="246" y="36" fontSize="12" fill="#6b7280">Terrace</text>
          <text x="246" y="246" fontSize="12" fill="#6b7280">Outside wall</text>
          {ROOMS.map((r, i) => (
            <g key={`room-${r.x}-${r.y}`}>
              <text x={r.x + 40} y={r.y + 50} fontSize="12" fill="#9ca3af" textAnchor="middle">Room {i + 1}</text>
              <g opacity={splitIn(i) * (1 - out)}>
                <rect x={r.x + 8} y={r.y + 8} width="44" height="12" rx="4" fill="#fff" stroke="#6b7280" />
                <line x1={r.x + 52} x2={r.x + 70 + (r.x > 100 ? 18 : 118)} y1={r.y + 14} y2={r.y + 14} stroke="#b45309" strokeWidth="2" />
              </g>
              <g opacity={splitIn(i) * (1 - out)}>
                <rect x="252" y={r.y + 4 + (r.x > 100 ? 30 : 0)} width="40" height="26" rx="4" fill="#fff" stroke="#6b7280" strokeWidth="1.5" />
                <circle cx="266" cy={r.y + 17 + (r.x > 100 ? 30 : 0)} r="8" fill="none" stroke="#9ca3af" />
              </g>
              <g opacity={grille}>
                <rect x={r.x + 16} y={r.y - 6} width="48" height="6" rx="2" fill="#dbeafe" stroke="#2563eb" />
              </g>
            </g>
          ))}
          <g opacity={vrv} transform={`translate(0 ${(1 - vrv) * -10})`}>
            <rect x="60" y="8" width="64" height="30" rx="5" fill="#fff" stroke="#2563eb" strokeWidth="2" />
            <circle cx="78" cy="23" r="9" fill="none" stroke="#2563eb" />
            <circle cx="104" cy="23" r="9" fill="none" stroke="#2563eb" />
          </g>
          <path d="M92 38V64H220M92 64V144H220" fill="none" stroke="#2563eb" strokeWidth="3"
            pathLength="1" strokeDasharray="1" strokeDashoffset={1 - pipe} />
        </svg>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className={`rounded-xl border p-2.5 ${t < 0.5 ? 'border-gray-900' : 'border-gray-200'}`}>
            <p className="text-[13px] text-gray-600">Split ACs</p>
            <p className="text-[20px] font-extrabold text-gray-900">{t < 0.5 ? outdoor : 4} outdoor units</p>
          </div>
          <div className={`rounded-xl border p-2.5 ${t >= 0.5 ? 'border-blue-600' : 'border-gray-200'}`}>
            <p className="text-[13px] text-gray-600">VRV</p>
            <p className="text-[20px] font-extrabold text-gray-900">1 outdoor unit</p>
          </div>
        </div>
      </div>
    </Explainer>
  );
}
